import { useRef, useState } from 'react';
import { Link, Plus } from 'lucide-react';
import { LinkRow } from '../LinkRow';
import type { LinkEntry } from '@/lib/links';

export interface LinksSectionProps {
  /** The saved links. Owned by the parent form. */
  links: LinkEntry[];
  /** Emits the next saved-links array whenever a row is saved/edited/removed. */
  onChange: (links: LinkEntry[]) => void;
}

/**
 * The "Links" form section: a list of saved {@link LinkRow}s plus any unsaved
 * "Add link" drafts, and an always-visible "Add link +" affordance. Drafts
 * live in local state so dismissing an unsaved row is silent (no parent
 * mutation, no toast); saving a draft appends it to the parent's links.
 */
export function LinksSection({ links, onChange }: LinksSectionProps) {
  // Draft rows added via "Add link" but not yet saved. Negative keys keep
  // them distinct from saved-row keys. A ref (not state) so rapid clicks
  // each get a unique key without a stale-closure read.
  const [drafts, setDrafts] = useState<number[]>([]);
  const nextDraftKey = useRef(-1);

  function updateAt(i: number, entry: LinkEntry) {
    const next = links.slice();
    next[i] = entry;
    onChange(next);
  }
  function removeAt(i: number) {
    onChange(links.filter((_, idx) => idx !== i));
  }
  function addDraft() {
    const key = nextDraftKey.current;
    nextDraftKey.current -= 1;
    setDrafts((d) => [...d, key]);
  }
  function saveDraft(key: number, entry: LinkEntry) {
    setDrafts((d) => d.filter((k) => k !== key));
    onChange([...links, entry]);
  }
  function dismissDraft(key: number) {
    setDrafts((d) => d.filter((k) => k !== key));
  }

  return (
    <div className="flex w-full flex-col gap-1.5">
      <span className="flex items-center gap-1 text-sm font-medium leading-5 text-foreground">
        <Link
          size={20}
          className="text-icon-default"
        />
        Links
      </span>

      <div className="flex flex-col gap-1.5">
        {links.map((link, i) => (
          // Index key (not url): handlers below address rows by index, the
          // list isn't reorderable, and two saved links can share a url —
          // a url key would collide and mis-target edits/deletes.
          <LinkRow
            key={`saved-${i}`}
            link={link}
            onSave={(entry) => updateAt(i, entry)}
            onDelete={() => removeAt(i)}
          />
        ))}
        {drafts.map((key) => (
          <LinkRow
            key={`draft-${key}`}
            link={{ title: null, url: '' }}
            isDraft
            onSave={(entry) => saveDraft(key, entry)}
            onDelete={() => dismissDraft(key)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addDraft}
        className="inline-flex items-center gap-1.5 self-start rounded-sm px-1.5 py-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        Add link
        <Plus size={16} />
      </button>
    </div>
  );
}
